import type {
  Page,
  TyreBrandInput,
  TyreBrandListQuery,
  TyreBrandView,
  TyreDetail,
  TyreInput,
  TyreListQuery,
  TyreSizeInput,
  TyreSizeListQuery,
  TyreSizeView,
  TyreView,
  VehicleTyreLayout,
} from '@chukua/common';
import { api } from '../../../../shared/shared.api';
import { toListParams } from '../../../../shared/policies/list-params';

export type { TyreDetail, TyreView, VehicleTyreLayout };

/** The moves a tyre takes as `POST /tyres/:id/<action>`. */
export type TyreAction = 'fit' | 'rotate' | 'wear' | 'mark-worn' | 'repair' | 'retread' | 'remove' | 'scrap' | 'replace';

export function listTyres(query: TyreListQuery): Promise<Page<TyreView>> {
  return api<Page<TyreView>>(`/tyres?${toListParams(query)}`);
}

export function getTyre(id: string): Promise<TyreDetail> {
  return api<TyreDetail>(`/tyres/${id}`);
}

export function registerTyre(input: TyreInput): Promise<TyreView> {
  return api<TyreView>('/tyres', { method: 'POST', body: JSON.stringify(input) });
}

export function updateTyre(id: string, input: TyreInput): Promise<TyreView> {
  return api<TyreView>(`/tyres/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export function listTyreBrands(query: TyreBrandListQuery): Promise<Page<TyreBrandView>> {
  return api<Page<TyreBrandView>>(`/tyre-brands?${toListParams(query)}`);
}

/** Adds the brand, or answers the existing one when its name is already there ignoring case and spacing; 409 when that one is deactivated. */
export function createTyreBrand(input: TyreBrandInput): Promise<TyreBrandView> {
  return api<TyreBrandView>('/tyre-brands', { method: 'POST', body: JSON.stringify(input) });
}

export function listTyreSizes(query: TyreSizeListQuery): Promise<Page<TyreSizeView>> {
  return api<Page<TyreSizeView>>(`/tyre-sizes?${toListParams(query)}`);
}

/** Adds the size, or answers the existing one when its name is already there ignoring case and spacing; 409 when that one is deactivated. */
export function createTyreSize(input: TyreSizeInput): Promise<TyreSizeView> {
  return api<TyreSizeView>('/tyre-sizes', { method: 'POST', body: JSON.stringify(input) });
}

export async function moveTyre(id: string, action: TyreAction, body: Record<string, unknown>): Promise<void> {
  await api<unknown>(`/tyres/${id}/${action}`, { method: 'POST', body: JSON.stringify(body) });
}

export function getVehicleTyreLayout(kind: 'truck' | 'trailer', id: string): Promise<VehicleTyreLayout> {
  return api<VehicleTyreLayout>(`/${kind}s/${id}/tyre-layout`);
}
